/**
 * useChallenges Hook
 * Manages fetching and CRUD operations for challenges
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  Challenge,
  ChallengeWithStatus,
  CreateChallengeInput,
  ChallengeCompletion,
  ChallengeConfig,
} from "../types";

/** Database row type for challenges */
interface ChallengeRow {
  id: string;
  weekend_id: string | null;
  title: string;
  description: string | null;
  challenge_type: string;
  target_type: string;
  visibility: string;
  visible_to_team_id: string | null;
  visible_to_user_ids: string[] | null;
  has_points: boolean;
  points: number;
  bonus_points: number;
  available_from: string | null;
  available_until: string | null;
  time_limit_minutes: number;
  status: string;
  config: ChallengeConfig;
  linked_event_id: string | null;
  reveal_targets: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Database row type for completions */
interface CompletionRow {
  id: string;
  challenge_id: string;
  completed_by: string;
  team_id: string | null;
  submission: Record<string, unknown>;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  points_earned: number;
  bonus_earned: number;
  started_at: string | null;
  completed_at: string;
  created_at: string;
}

interface UseChallengesOptions {
  /** Filter by status */
  status?: "draft" | "active" | "completed" | "archived";
  /** Filter by linked event */
  linkedEventId?: string;
  /** Only show unlinked challenges */
  unlinkedOnly?: boolean;
  /** Include user's completion status */
  includeCompletions?: boolean;
}

interface UseChallengesReturn {
  challenges: ChallengeWithStatus[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createChallenge: (input: CreateChallengeInput) => Promise<Challenge | null>;
  updateChallenge: (id: string, updates: Partial<CreateChallengeInput>) => Promise<boolean>;
  deleteChallenge: (id: string) => Promise<boolean>;
  linkToEvent: (challengeId: string, eventId: string, revealTargets: string[]) => Promise<boolean>;
  unlinkFromEvent: (challengeId: string) => Promise<boolean>;
}

/**
 * Hook for managing challenges
 */
export const useChallenges = (options: UseChallengesOptions = {}): UseChallengesReturn => {
  const { profile, activeWeekend, userTeam } = useAuth();
  const [challenges, setChallenges] = useState<ChallengeWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { status, linkedEventId, unlinkedOnly, includeCompletions = true } = options;

  /**
   * Fetch challenges based on filters
   */
  const fetchChallenges = useCallback(async () => {
    if (!profile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      let query = supabase
        .from("challenges")
        .select("*")
        .order("created_at", { ascending: false });

      // Apply filters
      if (status) {
        query = query.eq("status", status);
      }

      if (linkedEventId) {
        query = query.eq("linked_event_id", linkedEventId);
      }

      if (unlinkedOnly) {
        query = query.is("linked_event_id", null);
      }

      if (activeWeekend?.id) {
        query = query.eq("weekend_id", activeWeekend.id);
      }

      const { data: challengesData, error: challengesError } = await query;

      if (challengesError) throw challengesError;

      // Fetch completions if needed
      let completionsMap: Record<string, ChallengeCompletion> = {};

      if (includeCompletions && challengesData && challengesData.length > 0) {
        const challengeIds = (challengesData as ChallengeRow[]).map((c: ChallengeRow) => c.id);

        // Get user's completions (or team's completions)
        let completionsQuery = supabase
          .from("challenge_completions")
          .select("*")
          .in("challenge_id", challengeIds);

        // For team challenges, get team completions
        if (userTeam?.id) {
          completionsQuery = completionsQuery.or(
            `completed_by.eq.${profile.id},team_id.eq.${userTeam.id}`
          );
        } else {
          completionsQuery = completionsQuery.eq("completed_by", profile.id);
        }

        const { data: completionsData } = await completionsQuery;

        if (completionsData) {
          (completionsData as CompletionRow[]).forEach((completion: CompletionRow) => {
            completionsMap[completion.challenge_id] = completion as unknown as ChallengeCompletion;
          });
        }
      }

      // Map challenges with status
      const challengesWithStatus: ChallengeWithStatus[] = ((challengesData || []) as ChallengeRow[]).map(
        (challenge: ChallengeRow) => {
          const completion = completionsMap[challenge.id] || null;
          const isCompleted = completion?.status === "approved" || completion?.status === "auto_approved";

          // Check if user can complete this challenge
          const now = new Date();
          const availableFrom = challenge.available_from ? new Date(challenge.available_from) : null;
          const availableUntil = challenge.available_until ? new Date(challenge.available_until) : null;
          const isAvailable =
            (!availableFrom || now >= availableFrom) &&
            (!availableUntil || now <= availableUntil);

          return {
            ...challenge,
            completion,
            is_completed: isCompleted,
            can_complete: isAvailable && !isCompleted && challenge.status === "active",
          } as ChallengeWithStatus;
        }
      );

      setChallenges(challengesWithStatus);
    } catch (err: any) {
      console.error("Error fetching challenges:", err);
      setError(err.message || "Kon uitdagingen niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [profile?.id, activeWeekend?.id, userTeam?.id, status, linkedEventId, unlinkedOnly, includeCompletions]);

  /**
   * Create a new challenge
   */
  const createChallenge = useCallback(
    async (input: CreateChallengeInput): Promise<Challenge | null> => {
      if (!profile?.id) return null;

      try {
        const { data, error: createError } = await supabase
          .from("challenges")
          .insert({
            ...input,
            weekend_id: input.weekend_id || activeWeekend?.id,
            created_by: profile.id,
          })
          .select()
          .single();

        if (createError) throw createError;

        // Refresh the list
        await fetchChallenges();

        return data as Challenge;
      } catch (err: any) {
        console.error("Error creating challenge:", err);
        setError(err.message || "Kon uitdaging niet aanmaken");
        return null;
      }
    },
    [profile?.id, activeWeekend?.id, fetchChallenges]
  );

  /**
   * Update an existing challenge
   */
  const updateChallenge = useCallback(
    async (id: string, updates: Partial<CreateChallengeInput>): Promise<boolean> => {
      try {
        const { error: updateError } = await supabase
          .from("challenges")
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq("id", id);

        if (updateError) throw updateError;

        await fetchChallenges();
        return true;
      } catch (err: any) {
        console.error("Error updating challenge:", err);
        setError(err.message || "Kon uitdaging niet bijwerken");
        return false;
      }
    },
    [fetchChallenges]
  );

  /**
   * Delete a challenge
   */
  const deleteChallenge = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error: deleteError } = await supabase
          .from("challenges")
          .delete()
          .eq("id", id);

        if (deleteError) throw deleteError;

        await fetchChallenges();
        return true;
      } catch (err: any) {
        console.error("Error deleting challenge:", err);
        setError(err.message || "Kon uitdaging niet verwijderen");
        return false;
      }
    },
    [fetchChallenges]
  );

  /**
   * Link a challenge to an event
   */
  const linkToEvent = useCallback(
    async (challengeId: string, eventId: string, revealTargets: string[]): Promise<boolean> => {
      try {
        const { error: linkError } = await supabase
          .from("challenges")
          .update({
            linked_event_id: eventId,
            reveal_targets: revealTargets,
            updated_at: new Date().toISOString(),
          })
          .eq("id", challengeId);

        if (linkError) throw linkError;

        await fetchChallenges();
        return true;
      } catch (err: any) {
        console.error("Error linking challenge:", err);
        setError(err.message || "Kon uitdaging niet koppelen");
        return false;
      }
    },
    [fetchChallenges]
  );

  /**
   * Unlink a challenge from an event
   */
  const unlinkFromEvent = useCallback(
    async (challengeId: string): Promise<boolean> => {
      try {
        const { error: unlinkError } = await supabase
          .from("challenges")
          .update({
            linked_event_id: null,
            reveal_targets: [],
            updated_at: new Date().toISOString(),
          })
          .eq("id", challengeId);

        if (unlinkError) throw unlinkError;

        await fetchChallenges();
        return true;
      } catch (err: any) {
        console.error("Error unlinking challenge:", err);
        setError(err.message || "Kon uitdaging niet ontkoppelen");
        return false;
      }
    },
    [fetchChallenges]
  );

  // Initial fetch
  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return {
    challenges,
    isLoading,
    error,
    refetch: fetchChallenges,
    createChallenge,
    updateChallenge,
    deleteChallenge,
    linkToEvent,
    unlinkFromEvent,
  };
};

export default useChallenges;

